import { AnimatedNumber } from "@/components/AnimatedNumber";
import { SectionLabel } from "@/components/SectionLabel";
import { WordReveal } from "@/components/WordReveal";
import { useProfile } from "@/hooks/useProfile";
import { usePublications } from "@/hooks/usePublications";

const FOCUS = [
  "Kunstig intelligens",
  "Beslutningsalgoritmer",
  "Tsetlin-maskiner",
  "Beredskap & krisehåndtering",
  "Cybersikkerhet",
  "Teknologi & samfunn",
];

const FALLBACK_PUBLICATION_COUNT = 67;
const FALLBACK_PROFILE = {
  bio: [
    "Jeg er førsteamanuensis ved Institutt for informasjonssystemer, Universitetet i Agder, med tilknytning til Centre for Integrated Emergency Management (CIEM) og Centre for Artificial Intelligence Research (CAIR).",
    "Arbeidet mitt ligger i skjæringspunktet mellom kunstig intelligens, organisasjoner og samfunn. Med faglig forankring i informasjonssystemer utvikler jeg algoritmer og læringssystemer som kan forklares, vurderes og brukes i praksis.",
  ],
  stats: {
    years_researching: 10,
    students_supervised: 123,
  },
};

export function About() {
  const { data, isLoading } = useProfile();
  const { data: publications } = usePublications();
  const publicationCount = getVisiblePublicationCount(
    data?.stats.publications,
    publications?.length,
  );
  const profile = data ?? FALLBACK_PROFILE;

  return (
    <section
      id="about"
      className="border-t border-line px-6 py-20 md:px-10 lg:px-16"
      aria-labelledby="about-h"
    >
      <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <SectionLabel index={1}>Om</SectionLabel>
          <WordReveal
            as="h2"
            id="about-h"
            className="mt-5 block max-w-xl font-display text-5xl font-normal leading-[1.02] md:text-6xl"
          >
            Kunstig intelligens som gjør nytte — for mennesker, organisasjoner og samfunnet rundt
            oss.
          </WordReveal>
        </div>

        <div>
          {isLoading && <p className="text-muted">Laster …</p>}

          <div className="space-y-14">
            <div className="space-y-7">
              {profile.bio.map((paragraph, i) => (
                <p key={i} className="max-w-prose text-lg leading-relaxed text-muted md:text-xl">
                  {paragraph}
                </p>
              ))}
            </div>

            <figure className="border-l border-line pl-6 md:pl-8">
              <blockquote className="text-fg/90 font-display text-2xl italic leading-snug md:text-3xl">
                &ldquo;Det interessante spørsmålet er ikke om algoritmen lærer &mdash; men om vi kan
                stole på den når den får ansvar.&rdquo;
              </blockquote>
            </figure>

            <dl className="grid gap-4 sm:grid-cols-3">
              <Stat label="Publikasjoner" value={publicationCount} approximate />
              <Stat label="Forskningsår" value={profile.stats.years_researching} />
              <Stat
                label="Veiledede studenter"
                value={profile.stats.students_supervised}
                approximate
              />
            </dl>

            <div>
              <p className="mb-3 font-mono text-xs uppercase tracking-widest text-muted">
                Fagområder
              </p>
              <ul className="flex flex-wrap gap-2">
                {FOCUS.map((item) => (
                  <li
                    key={item}
                    className="border border-line bg-panel px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-muted"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function getVisiblePublicationCount(profileCount = 0, publicationListCount = 0) {
  if (profileCount > 0) {
    return profileCount;
  }
  if (publicationListCount > 0) {
    return publicationListCount;
  }
  return FALLBACK_PUBLICATION_COUNT;
}

function Stat({
  label,
  value,
  approximate = false,
}: {
  label: string;
  value: number;
  approximate?: boolean;
}) {
  return (
    <div className="card-lift border border-line bg-panel px-5 py-6">
      <dt className="font-mono text-xs uppercase tracking-widest text-muted">{label}</dt>
      <dd className="mt-2 flex items-baseline gap-1 font-display text-5xl font-normal text-fg">
        {approximate && (
          <span aria-hidden className="text-muted">
            ~
          </span>
        )}
        <AnimatedNumber value={value} />
      </dd>
    </div>
  );
}
